/**
 *  An iframe that sandboxes wix-dealer ads rendered by our da-wix-dealer app
 *  the app communicates with this component using the postMessage api, telling
 *  it whether an ad was loaded ('ad-filled') or none is available ('ad-empty')
 */ import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import React, { useCallback, useContext, useState } from 'react';
import classnames from 'classnames';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import { useSelectByTheme } from '@wix/da-react-context/pkg/ThemeContext';
import { useMessageListener } from '@wix/da-hooks/pkg/useMessageListener';
import ddt from '@wix/da-ddt';
import { useIsMounted } from '@wix/da-hooks/pkg/useIsMounted';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import s from './DealerAdIframe.scss';
const IFRAME_TITLE = 'DealerAdIframe';
const DEALER_RUNNING_LOCALLY = process.env.NODE_ENV === 'development';
const ADS_ENDPOINT = DEALER_RUNNING_LOCALLY ? 'http://localhost:5000/_nsfgfb' : 'https://www.deviantart.com/_nsfgfb';
const WIX_DEALER_MESSAGE_TYPE = 'dealerAd';
// this next id can be used to test the system and should always be available
export const TEST_REALESTATEID = 'd4c58bed-9276-439f-b040-36472ccfba3c';
const DealerAdIframe = (props)=>{
    const { realEstateId, userGuid, biClientId, biClientIdSigned, showAndCollapse, className, extraParams = {}, disableLazy } = props;
    const noDealerAds = useFlag('no_dealer_ads'); // do not show ads at all, useful for storybook and such
    const fakeAds = useFlag('yumeruby'); // show a placeholder instead of the iframe, useful for debugging
    const isMounted = useIsMounted();
    const isMobile = useContext(MobileContext);
    const theme = useSelectByTheme({
        dark: 'dark',
        light: 'light',
        green: 'green'
    });
    const [collapsed, setCollapsed] = useState(false);
    const handler = useCallback((data)=>{
        ddt.log('dealer', 'Got command from iframe', data);
        switch(data.command){
            case 'ad-empty':
            case 'ad-filled':
                if (fakeAds) {
                    setCollapsed(false);
                } else {
                    setCollapsed(showAndCollapse ? data.command === 'ad-empty' : data.command !== 'ad-filled');
                }
                break;
            default:
                break;
        }
    }, [
        fakeAds,
        showAndCollapse
    ]);
    useWixDealerListener(realEstateId, handler);
    if (noDealerAds) {
        return null;
    }
    const classNames = classnames(s['root'], collapsed && s['collapsed'], className);
    const paramsToSend = new URLSearchParams(Object.entries(_object_spread({
        realEstateId,
        theme,
        userGuid,
        biClientId,
        biClientIdSigned,
        newApi: 2,
        platform: isMobile ? 'mobile' : 'desktop'
    }, extraParams)).filter((e)=>e[1]));
    const iframeProps = {
        scrolling: 'no',
        frameBorder: '0',
        style: {
            border: '0px',
            width: '100%',
            height: '100%'
        }
    };
    if (fakeAds) {
        return /*#__PURE__*/ React.createElement("div", {
            style: {
                background: 'lightskyblue',
                overflow: 'auto'
            },
            className: classNames
        }, realEstateId);
    }
    if (!isMounted) {
        return null;
    }
    return /*#__PURE__*/ React.createElement("iframe", _object_spread_props(_object_spread({
        loading: disableLazy ? 'eager' : 'lazy',
        className: classNames
    }, iframeProps), {
        title: IFRAME_TITLE,
        src: `${ADS_ENDPOINT}/?${paramsToSend}`
    }));
};
DealerAdIframe.displayName = 'DealerAdIframe';
// ------------
/** Listen to 'message' events from an iframe containing the da-wix-dealer
 *  app and filtering by a realEstateId */ export function useWixDealerListener(realEstateId, handler) {
    useMessageListener((event)=>{
        if (event.data && event.data.type === WIX_DEALER_MESSAGE_TYPE && event.data.realEstateId === realEstateId) {
            handler(event.data);
        }
    });
}
// never, ever re-render the iframe
export default /*#__PURE__*/ React.memo(DealerAdIframe, ()=>true);
